import type { QueryClient } from "@tanstack/react-query";

export interface QueuedInvalidationOptions {
  /** Delay before flushing the queue (default 200ms). */
  flushDelayMs?: number;
  /** Enable debug logging for queued/flushed invalidations. */
  devLogging?: boolean;
  /** Logger with a `debug` method. Falls back to a no-op. */
  logger?: Pick<Console, "debug">;
}

export type QueueInvalidate = (key: unknown[]) => void;

type SharedEntry = {
  queue: Set<string>;
  timer: ReturnType<typeof setTimeout> | null;
  options: Required<
    Pick<QueuedInvalidationOptions, "flushDelayMs" | "devLogging" | "logger">
  >;
};

const NO_OP_DEBUG_LOGGER: Pick<Console, "debug"> = { debug: () => {} };

const sharedMap: WeakMap<QueryClient, SharedEntry> = new WeakMap();

/**
 * Factory that creates a batched invalidation function tied to a `QueryClient`.
 *
 * - De-dupes keys in a `Set` using JSON serialization
 * - Flushes after a small delay, calling `invalidateQueries` for each key
 * - Uses `exact: false` so subtree keys also invalidate
 */
export function createQueuedInvalidator(
  queryClient: QueryClient,
  options: QueuedInvalidationOptions = {}
): { queueInvalidate: QueueInvalidate; cleanup: () => void } {
  const merged: SharedEntry["options"] = {
    flushDelayMs: options.flushDelayMs ?? 200,
    devLogging: options.devLogging ?? false,
    logger: options.logger ?? NO_OP_DEBUG_LOGGER,
  };

  let entry = sharedMap.get(queryClient);
  if (!entry) {
    entry = { queue: new Set<string>(), timer: null, options: merged };
    sharedMap.set(queryClient, entry);
  } else {
    entry.options = merged;
  }

  const queueInvalidate: QueueInvalidate = (key: unknown[]) => {
    const { queue } = entry!;
    const { devLogging, logger } = entry!.options;
    try {
      const sk = JSON.stringify(key);
      queue.add(sk);
      if (devLogging) {
        logger.debug("[RT] queued invalidate", key, `size=${queue.size}`);
      }
      if (!entry!.timer) {
        entry!.timer = setTimeout(() => {
          const keys = Array.from(queue).map((s) => JSON.parse(s) as unknown[]);
          queue.clear();
          entry!.timer = null;
          if (devLogging) {
            logger.debug(`[RT] flushing ${keys.length} invalidations`, keys);
          }
          keys.forEach((k) => {
            queryClient.invalidateQueries({ queryKey: k, exact: false });
          });
        }, entry!.options.flushDelayMs);
      }
    } catch (e) {
      if (entry!.options.devLogging) {
        entry!.options.logger.debug(
          "[RT] queue failed, invalidating directly",
          key,
          e
        );
      }
      queryClient.invalidateQueries({ queryKey: key, exact: false });
    }
  };

  const cleanup = () => {
    if (entry!.timer) {
      clearTimeout(entry!.timer);
      entry!.timer = null;
    }
    entry!.queue.clear();
  };

  return { queueInvalidate, cleanup };
}
